import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { orderAPI } from '../../utils/api';
import { ArrowLeft, CreditCard, Truck, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, loading: cartLoading } = useCart();
  const { user } = useAuth();
  const [shipping, setShipping] = useState({
    full_name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Kosovë',
  });
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (user) {
      setShipping((prev) => ({ ...prev, full_name: user.name || '', email: user.email || prev.email, phone: user.phone || prev.phone }));
    }
  }, [user]);

  const { mutateAsync, isLoading: isPlacingOrder } = useMutation({
    mutationFn: (payload) => orderAPI.create(payload),
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Gabim gjatë krijimit të porosisë');
    },
  });

  const subtotal = cart?.subtotal || 0;
  const shippingCost = subtotal >= 49 ? 0 : 5.99;
  const total = subtotal + shippingCost;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cart?.items?.length) {
      toast.error('Shporta është bosh');
      return;
    }

    if (!shipping.full_name || !shipping.address || !shipping.city || !shipping.postal_code) {
      toast.error('Plotësoni të gjitha fushat e dërgimit');
      return;
    }

    const { order_id } = await mutateAsync({
      items: cart.items.map((item) => ({ product_id: item.product_id, quantity: item.quantity })),
      shipping_address: shipping,
      billing_address: shipping,
      payment_method: paymentMethod,
      notes,
    });

    navigate(`/order-confirm/${order_id}`);
  };

  if (cartLoading) return <div className="loading-screen"><div className="spinner" /></div>;

  if (!cart?.items?.length) {
    return (
      <div className="page-container" style={{ paddingTop: 80, paddingBottom: 80 }}>
        <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 'var(--radius-xl)', border: '1px solid var(--cream-dark)' }}>
          <h2 style={{ marginBottom: 12 }}>Shporta është bosh</h2>
          <p style={{ color: 'var(--text-light)', marginBottom: 24 }}>Shtoni produkte në shportë për të vazhduar me pagesën.</p>
          <Link to="/shop" className="btn btn-primary">Shko te Dyqani</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ paddingTop: 80, paddingBottom: 80 }}>
      <div style={{ marginBottom: 24 }}>
        <Link to="/cart" className="btn btn-ghost"><ArrowLeft size={16} /> Kthehu te Shporta</Link>
      </div>
      <div className="page-header">
        <div>
          <p className="section-eyebrow">Pagesë</p>
          <h1 className="page-title">Përfundoni porosinë</h1>
          <p className="page-sub">Plotësoni të dhënat e dërgimit dhe zgjidhni mënyrën e pagesës.</p>
        </div>
      </div>

      <div className="checkout-grid">
        <form className="checkout-form" onSubmit={handleSubmit}>
          <div className="card">
            <div className="section-eyebrow">Adresa e Dërgimit</div>
            <div className="form-group">
              <label className="form-label">Emri i plotë</label>
              <input className="form-input" value={shipping.full_name} onChange={(e) => setShipping({ ...shipping, full_name: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={shipping.email} disabled />
            </div>
            <div className="form-group">
              <label className="form-label">Telefoni</label>
              <input className="form-input" value={shipping.phone} onChange={(e) => setShipping({ ...shipping, phone: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">Adresa</label>
              <input className="form-input" value={shipping.address} onChange={(e) => setShipping({ ...shipping, address: e.target.value })} required />
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Qyteti</label>
                <input className="form-input" value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Kodi postar</label>
                <input className="form-input" value={shipping.postal_code} onChange={(e) => setShipping({ ...shipping, postal_code: e.target.value })} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Shteti</label>
              <input className="form-input" value={shipping.country} onChange={(e) => setShipping({ ...shipping, country: e.target.value })} required />
            </div>
          </div>

          <div className="card">
            <div className="section-eyebrow">Mënyra e pagesës</div>
            <div className="payment-options">
              <label className="payment-card">
                <input type="radio" name="payment" value="cod" checked={paymentMethod === 'cod'} onChange={() => setPaymentMethod('cod')} />
                <div>
                  <strong>Pagesë në dorëzim</strong>
                  <p>Pagesë kur dorëzohet porosia.</p>
                </div>
              </label>
              <label className="payment-card">
                <input type="radio" name="payment" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} />
                <div>
                  <strong>Pagesë me kartë</strong>
                  <p>Pagesë e sigurt online. Opsioni është simbolik për këtë version.</p>
                </div>
              </label>
            </div>
            <div className="form-group">
              <label className="form-label">Shënime</label>
              <textarea className="form-input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Instruksione të posaçme për dërgimin..." />
            </div>
          </div>

          <button className="btn btn-primary btn-full" type="submit" disabled={isPlacingOrder}>
            {isPlacingOrder ? 'Po përpunohet...' : 'Krijo porosinë'}
          </button>
        </form>

        <aside className="checkout-summary">
          <div className="card summary-card">
            <h2 className="section-title" style={{ marginBottom: 18 }}>Përmbledhja e porosisë</h2>
            <div className="summary-row"><span>Artikuj</span><span>{cart.item_count}</span></div>
            <div className="summary-row"><span>Nëntotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div className="summary-row"><span>Dërgimi</span><span>{shippingCost === 0 ? 'Falas' : `$${shippingCost.toFixed(2)}`}</span></div>
            <div className="summary-total"><span>Totali</span><span>${total.toFixed(2)}</span></div>
            {shippingCost > 0 && <p style={{ color: 'var(--text-light)', marginTop: 12 }}>Shtoni ${(49 - subtotal).toFixed(2)} më shumë për të fituar dërgim falas.</p>}
          </div>

          <div className="card summary-card">
            <h2 className="section-title" style={{ marginBottom: 18 }}>Produkte në shportë</h2>
            <div className="checkout-items">
              {cart.items.map((item) => {
                const images = (() => { try { return JSON.parse(item.images || '[]'); } catch { return [item.images]; } })();
                return (
                  <div key={item.id} className="checkout-item-row">
                    <img src={images[0] || 'https://via.placeholder.com/80'} alt={item.name} />
                    <div>
                      <div style={{ fontWeight: 600 }}>{item.name}</div>
                      <div style={{ color: 'var(--text-light)', fontSize: '.85rem' }}>{item.quantity} x ${Number(item.price).toFixed(2)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </aside>
      </div>

      <style>{`
        .checkout-grid { display: grid; grid-template-columns: 1.6fr 0.9fr; gap: 24px; }
        .checkout-form { display: flex; flex-direction: column; gap: 24px; }
        .checkout-summary { display: flex; flex-direction: column; gap: 24px; }
        .payment-options { display: grid; gap: 14px; }
        .payment-card { display: flex; gap: 14px; align-items: flex-start; padding: 18px; border: 1px solid var(--cream-dark); border-radius: var(--radius-xl); cursor: pointer; }
        .payment-card input { margin-top: 6px; }
        .payment-card strong { display: block; margin-bottom: 6px; }
        .summary-card { padding: 24px; border-radius: var(--radius-xl); border: 1px solid var(--cream-dark); }
        .summary-row { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
        .summary-total { display: flex; justify-content: space-between; gap: 12px; margin-top: 12px; font-size: 1.05rem; font-weight: 700; }
        .checkout-items { display: flex; flex-direction: column; gap: 16px; }
        .checkout-item-row { display: flex; gap: 12px; align-items: center; }
        .checkout-item-row img { width: 72px; height: 72px; object-fit: cover; border-radius: var(--radius-md); background: var(--cream); }
        .form-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        @media(max-width: 980px) { .checkout-grid { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}
